import { useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { AdditionalDriverAgreement } from '../AdditionalDriverAgreement';

const AllowedTripCard = styled.div`
  ${tw`p-4 mb-4 rounded-lg border border-border-default bg-white text-sm shadow-card last:mb-0`}
`;

const AllowedTripTitle = styled.div`
  ${tw`font-bold text-text-primary text-base mb-3`}
`;

const AllowedTripRow = styled.div`
  ${tw`flex justify-between gap-4 py-1.5 items-start`}
`;

const AllowedTripLabel = styled.span`
  ${tw`text-text-secondary flex-shrink-0`}
`;

const AllowedTripValue = styled.span`
  ${tw`text-text-primary font-semibold text-right break-all max-w-[58%]`}
`;

const Disclaimer = styled.p`
  ${tw`text-xs text-text-secondary mb-4 leading-relaxed`}
`;

/** Local clock: MM/DD/YYYY HH:mm:ss (24-hour) */
export function formatAddedYouOnLocal(value: unknown): string {
  if (value == null || value === '') return '—';
  const d = value instanceof Date ? value : new Date(value as string | number);
  if (isNaN(d.getTime())) return '—';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getMonth() + 1)}/${p(d.getDate())}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

export interface AllowedToDriveTripsContentProps {
  trips: any[];
  loading: boolean;
}

/**
 * List + disclaimer shown in Profile modal and on `/trips-as-additional-driver`.
 */
export function AllowedToDriveTripsContent({ trips, loading }: AllowedToDriveTripsContentProps) {
  const [acceptedIds, setAcceptedIds] = useState<string[]>([]);
  return (
    <>
      <Disclaimer>
        As an additional driver, you only see limited trip information here. The main driver who
        booked the car is the only person who can check in, check out, and pay for the trip.
      </Disclaimer>
      {loading && <p tw="text-sm text-text-secondary">Loading allowed trips…</p>}
      {!loading && trips.length === 0 && (
        <p tw="text-sm text-text-secondary">
          You are not listed as an additional driver on any trips.
        </p>
      )}
      {!loading &&
        trips.map((trip: any) => (
          <AllowedTripCard key={trip.tripId}>
            <AllowedTripTitle>
              {[trip.carYear, trip.carMake, trip.carModel].filter(Boolean).join(' ')}
            </AllowedTripTitle>
            <AllowedTripRow>
              <AllowedTripLabel>Trip ID</AllowedTripLabel>
              <AllowedTripValue>{trip.tripId}</AllowedTripValue>
            </AllowedTripRow>
            <AllowedTripRow>
              <AllowedTripLabel>Plate</AllowedTripLabel>
              <AllowedTripValue>{trip.plateNumber || 'Unavailable'}</AllowedTripValue>
            </AllowedTripRow>
            <AllowedTripRow>
              <AllowedTripLabel>Last 5 VIN</AllowedTripLabel>
              <AllowedTripValue>{trip.lastFiveVin || 'Unavailable'}</AllowedTripValue>
            </AllowedTripRow>
            <AllowedTripRow>
              <AllowedTripLabel>Main driver</AllowedTripLabel>
              <AllowedTripValue>{trip.mainDriverName || 'Unavailable'}</AllowedTripValue>
            </AllowedTripRow>
            <AllowedTripRow>
              <AllowedTripLabel>Added you on</AllowedTripLabel>
              <AllowedTripValue>{formatAddedYouOnLocal(trip.addedAsDriverAt)}</AllowedTripValue>
            </AllowedTripRow>
            <AllowedTripRow>
              <AllowedTripLabel>Start</AllowedTripLabel>
              <AllowedTripValue>{new Date(trip.startAt).toLocaleString()}</AllowedTripValue>
            </AllowedTripRow>
            <AllowedTripRow>
              <AllowedTripLabel>End</AllowedTripLabel>
              <AllowedTripValue>{new Date(trip.endAt).toLocaleString()}</AllowedTripValue>
            </AllowedTripRow>
            <AllowedTripRow>
              <AllowedTripLabel>Pickup city</AllowedTripLabel>
              <AllowedTripValue>{trip.pickupCity || 'City unavailable'}</AllowedTripValue>
            </AllowedTripRow>
            <AllowedTripRow>
              <AllowedTripLabel>Return city</AllowedTripLabel>
              <AllowedTripValue>{trip.returnCity || 'City unavailable'}</AllowedTripValue>
            </AllowedTripRow>
            {trip.agreementAccepted === false &&
              trip.agreementVersionId &&
              !acceptedIds.includes(trip.tripId) && (
                <AdditionalDriverAgreement
                  bookingId={trip.tripId}
                  versionId={trip.agreementVersionId}
                  onAccepted={() => setAcceptedIds(ids => [...ids, trip.tripId])}
                />
              )}
          </AllowedTripCard>
        ))}
    </>
  );
}
